import { Trophy } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { isKey, isBlank, keyRanks, playerColIndex, teamColIndex, rankStyle, sameName, toNum, minApps, qualifies } from "@/lib/braggersStats";

const SERIF = "'Cormorant Garamond', serif";
const findSet = (sets, key, i) => sets.find((d) => d.dataset === key) || sets[i];

function KeyTile({ label, value, rank, C, testid }) {
  const top = rank && rank <= 3;
  return (
    <div className="rounded-sm border p-3" data-testid={testid} style={{ background: C.elevated, borderColor: top ? C.gold : C.border }}>
      <p className="text-[10px] uppercase tracking-[0.16em] font-bold" style={{ color: C.silver }}>{label}</p>
      <p className="text-2xl font-semibold mt-1 tabular-nums" style={{ fontFamily: SERIF, color: top ? C.goldLight : C.text }}>{isBlank(value) ? "—" : value}</p>
      {top && <p className="mt-1 text-xs font-bold inline-flex items-center gap-1" style={{ color: C.gold }}><Trophy className="h-3 w-3" /> #{rank} overall</p>}
    </div>
  );
}

function Record({ analysis, name, C }) {
  const cols = analysis.columns;
  const pc = playerColIndex(cols);
  const ri = analysis.rows.findIndex((r) => sameName(r[pc], name));
  if (ri < 0) return <p className="text-sm" style={{ color: C.sub }}>No {analysis.title} record for this player.</p>;
  const row = analysis.rows[ri];
  const min = minApps(analysis);
  const ranks = keyRanks(cols, analysis.rows, min);
  const keys = cols.map((_, i) => i).filter((i) => isKey(cols[i]));
  const rest = cols.map((_, i) => i).filter((i) => i !== pc && i !== teamColIndex(cols) && !isKey(cols[i]));
  return (
    <>
      {keys.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {keys.map((i) => <KeyTile key={i} label={cols[i]} value={row[i]} rank={ranks[i]?.[ri]} C={C} testid={`player-card-key-${i}`} />)}
        </div>
      )}
      {!qualifies(cols, row, min) && (
        <p className="-mt-3 mb-6 text-xs" style={{ color: C.silver }} data-testid="player-card-min-note">Not ranked for gold highlights yet — needs {min}+ appearances.</p>
      )}
      <h4 className="text-xs uppercase tracking-[0.2em] font-bold mb-3" style={{ color: C.silver }}>{analysis.title}</h4>
      <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6" data-testid="player-card-record">
        {rest.map((i) => (
          <div key={i} className="flex justify-between gap-3 py-2 border-b text-sm" style={{ borderColor: C.border }}>
            <dt style={{ color: C.sub }}>{cols[i] || "—"}</dt>
            <dd className="text-right font-medium tabular-nums" style={{ color: C.text }}>{isBlank(row[i]) ? "—" : row[i]}</dd>
          </div>
        ))}
      </dl>
    </>
  );
}

function Breakdown({ breakdown, name, C }) {
  const cols = breakdown.columns;
  const pc = playerColIndex(cols);
  const ranks = keyRanks(cols, breakdown.rows, minApps(breakdown));
  const show = cols.map((_, i) => i).filter((i) => i !== pc);
  const hits = breakdown.rows.map((r, ri) => ri).filter((ri) => sameName(breakdown.rows[ri][pc], name));
  return (
    <div className="mt-8">
      <h4 className="text-xs uppercase tracking-[0.2em] font-bold mb-3" style={{ color: C.silver }}>{breakdown.title} ({hits.length})</h4>
      {hits.length ? (
        <div className="overflow-x-auto rounded-sm border" style={{ borderColor: C.border, background: C.bg }}>
          <table className="w-full text-sm border-collapse" data-testid="player-card-breakdown">
            <thead><tr>{show.map((i) => <th key={i} className="px-3 py-2.5 text-left font-semibold whitespace-nowrap" style={{ background: C.elevated, color: C.sub }}>{cols[i]}</th>)}</tr></thead>
            <tbody>
              {hits.map((ri, n) => (
                <tr key={ri} data-testid={`player-card-breakdown-row-${n}`}>
                  {show.map((i) => {
                    const v = breakdown.rows[ri][i];
                    const hl = rankStyle(ranks[i]?.[ri], C);
                    return <td key={i} className={`px-3 py-2 whitespace-nowrap ${toNum(v) !== null ? "text-right tabular-nums" : ""}`} style={{ color: C.text, borderTop: `1px solid ${C.border}`, ...(hl || {}) }}>{v}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="text-sm" style={{ color: C.sub }}>No breakdown rows for this player.</p>}
    </div>
  );
}

export function BraggersPlayerCard({ name, datasets, C, onClose }) {
  const analysis = findSet(datasets, "player_analysis", 0);
  const breakdown = findSet(datasets, "player_breakdown", 1);
  const team = (() => {
    if (!analysis || !name) return "";
    const pc = playerColIndex(analysis.columns);
    const tc = teamColIndex(analysis.columns);
    const row = analysis.rows.find((r) => sameName(r[pc], name));
    const v = tc >= 0 ? row?.[tc] : "";
    return v && toNum(v) === null ? `${analysis.columns[tc]}: ${v}` : "";
  })();
  return (
    <Dialog open={!!name} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="braggers-player-card"
        className="block max-w-4xl w-[calc(100%-1.5rem)] max-h-[90vh] overflow-y-auto overflow-x-hidden rounded-sm p-0 gap-0 [&>button]:text-[#F3F4F6] [&>button]:!bg-transparent [&>button]:top-5 [&>button]:right-5"
        style={{ background: C.surface, borderColor: C.border, color: C.text }}>
        <div className="h-1 w-full" style={{ background: C.gold }} />
        <div className="p-6 sm:p-8">
          <p className="text-[11px] tracking-[0.25em] uppercase font-bold mb-2" style={{ color: C.gold }}>Bragger's Cup Player Record</p>
          <DialogTitle className="text-3xl sm:text-4xl font-medium leading-tight" style={{ fontFamily: SERIF, color: C.goldLight }} data-testid="player-card-name">{name}</DialogTitle>
          <DialogDescription className="mt-1 mb-6 text-sm" style={{ color: C.sub }} data-testid="player-card-team">{team || "\u00a0"}</DialogDescription>
          {name && analysis && <Record analysis={analysis} name={name} C={C} />}
          {name && breakdown && <Breakdown breakdown={breakdown} name={name} C={C} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
